import type { AppType } from '@utilities-one/reipm-api-sdk';
import { DetailedError, hc, parseResponse } from 'hono/client';

import { getClientApiUrl } from '@/config/getEnv';
import { redirectToLoginOn401 } from '@/library/api/redirectToLoginOn401';

/**
 * Typed Hono RPC client for the reipm-api `/api/v2` routes.
 *
 * `AppType` is `typeof route` where `route` is the sub-app mounted at
 * `/api/v2` (reipm-api/src/api/index.ts). Its routes are declared relative
 * (`/health`, `/users`, …), and `VITE_API_URL` already ends in `/api/v2`,
 * so the base URL is passed straight through — e.g. `api.health.$get()`
 * resolves to `${VITE_API_URL}/health` and `api.users.me.$post()` to
 * `${VITE_API_URL}/users/me`.
 *
 * Data path is Hono RPC ONLY — there is intentionally no superjson/`actions`
 * proxy. Auth is a cookie httpOnly JWT, so every request sends
 * `credentials: 'include'` and never handles a token directly; a stale-session
 * 401 is caught by {@link redirectToLoginOn401}.
 *
 * Wrap calls with {@link parseResponse} so non-2xx responses throw
 * {@link DetailedError} (feed it to `extractApiError`)
 * and the promise resolves to the parsed, typed body. When the body carries
 * `Date` fields, use `parseResponseWithDates` instead to rehydrate them.
 */
const api = hc<AppType>(getClientApiUrl(), {
  init: {
    credentials: 'include',
  },
  fetch: (input: RequestInfo | URL, init?: RequestInit) => fetch(input, init).then(redirectToLoginOn401),
});

export { api, DetailedError, parseResponse };
